import { useCallback } from 'react';
import { useDemand } from '../../context/DemandContext';
import { useFileManagementContext } from '../../context/FileManagementContext';
import { findBlobUrls, setBlobUrl2BlobID } from '../../utils/blobUtils';

/**
 * 🎯 數據轉換 Hook
 * 處理複雜的數據轉換邏輯，將內部狀態轉換為保存格式
 */
export const useDataTransformation = () => {
  const { state } = useDemand();
  const { state: fileState } = useFileManagementContext();

  /**
   * 將當前狀態轉換為保存格式
   * @returns {Object} 轉換後的數據結構
   */
  const transformToSaveFormat = useCallback(() => {
    const { basic, ceremony, notes, live, price } = state;
    return {
      basic,
      ceremony,
      notes,
      live,
      price
    };
  }, [state]);

  /**
   * 將 API 資料轉換為 UI 狀態
   * @param {Object} apiData - API 返回的資料
   * @returns {Object} 轉換後的 UI 狀態
   */
  const transformFromApiFormat = useCallback(async (demandItem) => {
    try {
      console.log('[transformFromApiFormat] Processing API data:', demandItem);
      
      const {
        information: { jsonFile },
        blobs,
      } = demandItem;

      // 從 jsonFile.url 取得 version 資料
      const response = await fetch(jsonFile.url);
      const versionData = await response.json();
      const version = versionData.version || [];
      const latestVersionIndex = version.length - 1;

      console.log('[transformFromApiFormat] Version data:', version);

      if (latestVersionIndex < 0) {
        return null;
      }

      // 取得最新版本的資料
      const latestVersion = version[latestVersionIndex];
      const { basic, ceremony, notes, live, price } = latestVersion;

      // 處理基本資料
      const processedBasic = {
        ...basic,
        birthDate: basic.birthDate || null,
        funeralDate: basic.funeralDate || null,
      };

      // 🎯 整合檔案資訊：將 blob 資料與業務物件關聯
      const enrichedBlobs = blobs.map(blob => {
        // 從業務資料中找到對應的檔案業務物件
        const fileBusinessObject = findFileBusinessObjectById(latestVersion, blob.id);
        
        return {
          ...blob,
          // 如果找到業務物件，則補充業務資訊
          ...(fileBusinessObject && {
            name: fileBusinessObject?.fileName,
            duration: fileBusinessObject?.duration,
            originalWidth: fileBusinessObject?.originalWidth,
            originalHeight: fileBusinessObject?.originalHeight,
            // 可以根據需要添加更多業務屬性
          })
        };
      });

      console.log('[transformFromApiFormat] Processed data:', {
        basic: processedBasic,
        ceremony,
        notes,
        live,
        price,
        enrichedBlobs
      });

      return {
        basic: processedBasic,
        ceremony: ceremony || {},
        notes: notes || '',
        live: live || { isEnabled: false, roomCode: null, url: null },
        price: price || 0,
        sourceData: {
          version,
          jsonFileId: jsonFile.id,
          blobs: enrichedBlobs // 使用整合後的 blob 資料
        }
      };
    } catch (error) {
      console.error('[transformFromApiFormat] Error:', error);
      return null;
    }
  }, []);

  /**
   * 🔧 輔助函數：根據檔案ID在業務資料中找到對應的檔案業務物件
   * @param {Object} versionData - 版本數據
   * @param {string} fileId - 檔案ID
   * @returns {Object|null} 檔案業務物件或null
   */
  const findFileBusinessObjectById = useCallback((versionData, fileId) => {
    const searchInObject = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      
      // 如果是陣列，檢查是否為檔案業務物件陣列
      if (Array.isArray(obj)) {
        for (const item of obj) {
          if (item?.fileId === fileId) {
            return item;
          }
          // 遞歸搜索陣列中的物件
          const found = searchInObject(item);
          if (found) return found;
        }
        return null;
      }
      
      // 遞歸搜索物件的所有屬性
      for (const value of Object.values(obj)) {
        const found = searchInObject(value);
        if (found) return found;
      }
      
      return null;
    };
    
    return searchInObject(versionData);
  }, []);

  /**
   * 準備 JSON 檔案用於上傳
   * @param {Object} sourceData - 源數據
   * @returns {Blob} JSON Blob 檔案
   */
  const prepareJsonFile = useCallback((sourceData) => {
    const jsonString = JSON.stringify(sourceData);
    return new Blob([jsonString], { type: "application/json" });
  }, []);

  /**
   * 遞歸收集所有檔案 ID
   */
  const collectFileIds = useCallback((obj) => {
    const fileIds = new Set();

    const traverse = (data) => {
      if (!data || typeof data !== 'object') return;

      if (Array.isArray(data)) {
        // 檢查是否為檔案業務物件陣列
        if (data.length > 0 && data[0]?.fileId) {
          data.forEach(fileObj => fileIds.add(fileObj.fileId));
        } else {
          data.forEach(traverse);
        }
        return;
      }

      Object.values(data).forEach(traverse);
    };

    traverse(obj);
    return Array.from(fileIds);
  }, []);

  /**
   * 從源數據中提取需要上傳的檔案
   * @param {Object} sourceData - 源數據
   * @returns {Array} 需要上傳的檔案列表
   */
  const extractPendingUploadFiles = useCallback((sourceData) => {
    // 1. 從數據中收集所有檔案 ID
    const fileIds = collectFileIds(sourceData);
    console.log('Found file IDs:', fileIds);

    // 2. 從 FileManagementContext 中找出對應的檔案
    const pendingFiles = fileIds
      .map(fileId => fileState.find(file => file.id === fileId || file.uid === fileId))
      .filter(file => {
        // 只取本地且有原始檔案的檔案
        const isPending = file && file.isLocal && file.originFileObj;
        if (!isPending) {
          console.log('Skipping file:', file?.uid, 'because:', !file ? 'not found' : !file.isLocal ? 'not local' : 'no originFileObj');
        }
        return isPending;
      });

    console.log('Pending files:', pendingFiles);

    // 3. 轉換為上傳格式
    const uploadFiles = pendingFiles.map(file => ({
      ...file.originFileObj,
      uid: file.uid,
      _path: file._path
    }));

    console.log('Files to upload:', uploadFiles);
    return uploadFiles;
  }, [fileState, collectFileIds]);

  /**
   * 更新源數據中的檔案引用（從臨時 ID 轉換為伺服器 ID）
   * @param {Object} sourceData - 源數據
   * @param {Array} uploadedFiles - 已上傳的檔案映射，格式：[{ origin: { id }, newFile: { id }, fallbackMatching: { formField, uploadContext } }]
   * @returns {Object} 更新後的源數據
   */
  const updateFileReferences = useCallback((sourceData, uploadedFiles) => {
    console.log('[updateFileReferences] 更新源數據中的檔案引用（從臨時 ID 轉換為伺服器 ID） 參數:', { sourceData, uploadedFiles });
    
    // 🎯 增強版檔案引用更新，包含多重匹配策略
    const updatedData = JSON.parse(JSON.stringify(sourceData)); // 深拷貝
    const updateLog = []; // 記錄更新過程
    
    // 1️⃣ 主要匹配策略：按照檔案 ID 匹配
    const primaryMatched = new Set();
    uploadedFiles.forEach(({ origin, newFile, fallbackMatching }) => {
      if (origin.id) {
        const updated = updateFileIdInData(updatedData, origin.id, newFile.id);
        if (updated > 0) {
          primaryMatched.add(origin.id);
          updateLog.push({
            strategy: 'primary',
            originalId: origin.id,
            newId: newFile.id,
            count: updated,
            success: true
          });
          console.log(`[主要匹配] 成功更新檔案 ID: ${origin.id} -> ${newFile.id}, 更新了 ${updated} 次`);
        }
      }
    });

    // 2️⃣ 備用匹配策略：針對主要匹配失敗的檔案，使用位置匹配
    const unmatched = uploadedFiles.filter(({ origin }) => !primaryMatched.has(origin.id));
    if (unmatched.length > 0) {
      console.log(`[備用匹配] 有 ${unmatched.length} 個檔案需要備用匹配:`, unmatched);
      
      unmatched.forEach(({ origin, newFile, fallbackMatching }) => {
        const { formField, uploadContext, fileSignature } = fallbackMatching || {};
        
        if (formField && formField.length > 0) {
          // 🔄 策略 A：按照 formField 位置匹配
          const updated = updateFileByPosition(updatedData, formField, origin, newFile, uploadContext);
          if (updated) {
            updateLog.push({
              strategy: 'position',
              originalId: origin.id,
              newId: newFile.id,
              formField,
              success: true
            });
            console.log(`[位置匹配] 成功更新檔案: ${origin.id} -> ${newFile.id}, 位置: ${formField.join('.')}`);
            return;
          }
        }
        
        // 🔄 策略 B：按照檔案特徵匹配（檔案名稱、大小、類型）
        if (fileSignature) {
          const updated = updateFileBySignature(updatedData, fileSignature, newFile);
          if (updated) {
            updateLog.push({
              strategy: 'signature',
              originalId: origin.id,
              newId: newFile.id,
              signature: fileSignature,
              success: true
            });
            console.log(`[特徵匹配] 成功更新檔案: ${origin.id} -> ${newFile.id}, 檔案名: ${fileSignature.name}`);
            return;
          }
        }
        
        // 🚨 所有策略都失敗
        updateLog.push({
          strategy: 'failed',
          originalId: origin.id,
          newId: newFile.id,
          reason: '所有匹配策略都失敗',
          success: false
        });
        console.warn(`[匹配失敗] 無法更新檔案: ${origin.id} -> ${newFile.id}`);
      });
    }

    console.log('[updateFileReferences] 更新完成，詳細記錄:', updateLog);
    return updatedData;
  }, []);

  /**
   * 🔧 輔助函數：在數據中更新指定的檔案 ID
   * @param {Object} data - 要更新的數據
   * @param {string} oldId - 舊檔案 ID
   * @param {string} newId - 新檔案 ID
   * @returns {number} 更新的次數
   */
  const updateFileIdInData = useCallback((data, oldId, newId) => {
    let updateCount = 0;
    
    const traverse = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      
      if (Array.isArray(obj)) {
        obj.forEach(item => {
          if (item?.fileId === oldId) {
            item.fileId = newId;
            updateCount++;
          }
          traverse(item);
        });
      } else {
        Object.values(obj).forEach(traverse);
      }
    };
    
    traverse(data);
    return updateCount;
  }, []);

  /**
   * 🔧 輔助函數：按照 formField 位置更新檔案
   * @param {Object} data - 要更新的數據  
   * @param {Array} formField - 表單欄位位置
   * @param {Object} origin - 原始檔案資訊
   * @param {Object} newFile - 新檔案資訊
   * @param {Object} uploadContext - 上傳上下文
   * @returns {boolean} 是否更新成功
   */
  const updateFileByPosition = useCallback((data, formField, origin, newFile, uploadContext) => {
    try {
      // 按照 formField 路徑導航到檔案陣列
      let current = data;
      for (let i = 0; i < formField.length; i++) {
        const field = formField[i];
        
        // 處理陣列索引（如 actions 中的動態索引）
        if (field === 'actions' && i + 1 < formField.length) {
          // 尋找正確的 action，可能需要特殊處理
          const nextField = formField[i + 1];
          if (typeof nextField === 'number') {
            current = current[field]?.[nextField];
            i++; // 跳過下一個索引
          } else {
            // 按照 ID 或其他標識查找
            current = current[field];
          }
        } else {
          current = current[field];
        }
        
        if (!current) {
          console.warn(`[位置匹配] 路徑不存在: ${formField.slice(0, i + 1).join('.')}`);
          return false;
        }
      }
      
      // 確保到達的是檔案陣列
      if (!Array.isArray(current)) {
        console.warn(`[位置匹配] 最終位置不是陣列: ${formField.join('.')}`);
        return false;
      }
      
      // 在檔案陣列中尋找匹配的檔案（按照檔案特徵）
      const targetIndex = current.findIndex(fileObj => 
        fileObj?.fileName === origin.name ||
        fileObj?.fileId === origin.id ||
        (fileObj?.fileName && origin.name && fileObj.fileName.includes(origin.name.split('.')[0]))
      );
      
      if (targetIndex >= 0) {
        current[targetIndex].fileId = newFile.id;
        console.log(`[位置匹配] 在位置 ${formField.join('.')}[${targetIndex}] 更新檔案 ID: ${origin.id} -> ${newFile.id}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[位置匹配] 發生錯誤:', error);
      return false;
    }
  }, []);

  /**
   * 🔧 輔助函數：按照檔案特徵更新檔案
   * @param {Object} data - 要更新的數據
   * @param {Object} fileSignature - 檔案特徵
   * @param {Object} newFile - 新檔案資訊
   * @returns {boolean} 是否更新成功
   */
  const updateFileBySignature = useCallback((data, fileSignature, newFile) => {
    let updated = false;
    
    const traverse = (obj) => {
      if (!obj || typeof obj !== 'object' || updated) return;
      
      if (Array.isArray(obj)) {
        obj.forEach(item => {
          if (item?.fileName === fileSignature.name && 
              item?.fileId !== newFile.id) {
            item.fileId = newFile.id;
            updated = true;
            console.log(`[特徵匹配] 更新檔案: ${fileSignature.name} -> ${newFile.id}`);
            return;
          }
          traverse(item);
        });
      } else {
        Object.values(obj).forEach(traverse);
      }
    };
    
    traverse(data);
    return updated;
  }, []);

  /**
   * 驗證數據完整性
   * @param {Object} data - 要驗證的數據
   * @returns {Object} 驗證結果 { isValid, errors }
   */
  const validateData = useCallback((data) => {
    const errors = [];
    // 基本資料驗證
    if (!data.basic?.name?.trim()) {
      errors.push('需求單-親友姓名不能為空');
    }
    // 可以根據需要添加更多驗證規則
    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  /**
   * 計算數據大小（用於進度計算）
   * @param {Object} data - 數據
   * @returns {number} 數據大小（字節）
   */
  const calculateDataSize = useCallback((data) => {
    try {
      const jsonString = JSON.stringify(data);
      return new Blob([jsonString]).size;
    } catch (error) {
      console.error('計算數據大小失敗:', error);
      return 0;
    }
  }, []);

  /**
   * 獲取數據摘要（用於日誌記錄）
   * @param {Object} data - 數據
   * @returns {Object} 數據摘要
   */
  const getDataSummary = useCallback((data) => {
    const summary = {
      hasBasicData: !!data.basic,
      hasCeremonyData: !!data.ceremony,
      hasNotes: !!data.notes,
      ceremonyTypes: [],
      totalActions: 0,
      estimatedSize: calculateDataSize(data)
    };
    if (data.ceremony) {
      // 統計各種典禮類型
      ['public', 'family', 'farewell', 'liveStream'].forEach(type => {
        if (data.ceremony[type]) {
          summary.ceremonyTypes.push(type);
        }
      });
      // 統計動作數量
      if (data.ceremony.actions) {
        summary.totalActions = data.ceremony.actions.length;
      }
    }
    return summary;
  }, [calculateDataSize]);

  return {
    // 轉換方法
    transformToSaveFormat,
    transformFromApiFormat,
    prepareJsonFile,
    extractPendingUploadFiles,
    updateFileReferences,
    // 驗證方法
    validateData,
    // 工具方法
    calculateDataSize,
    getDataSummary,
    findFileBusinessObjectById,
    // 🆕 新增的輔助方法
    updateFileIdInData,
    updateFileByPosition,
    updateFileBySignature,
    // 當前狀態
    currentState: state
  };
}; 